import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Theme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FormProvider, useForm } from 'react-hook-form';
import { makeStyles } from 'tss-react/mui';
import { yupResolver } from '@hookform/resolvers/yup';

import FormSubmitButton from '../../../components/buttons/FormSubmitButton';
import FormCancelButton from '../../../components/buttons/FormCancelButton';
import Loader from '../../../components/Loader';
import FormSelectField from '../../../components/formFields/FormSelectField';
import FormTextField from '../../../components/formFields/FormTextField';
import { SECONDARY_FIRM_ROLES_OPTIONS } from '../../../constants/constants';
import { attorneyFeeEditFormSchema } from '../../../helpers/formSchemas/formSchemas';
import { ATTORNEY_FEE_EDIT_FORM_DEFAULT_VALUES } from '../../../helpers/formSchemas/formDefaultValues';
import { useEffect, useState } from 'react';
import { AttorneyFeeEditFormSubmitProps } from '../types';

export interface AttorneyFeePopupData {
    id: number;
    name: string;
    fee: number;
    role: string;
    disposition: string;
    paymentMethod: string;
}
interface EditAttorneyFeePopupProps {
    isOpen: boolean;
    loading?: boolean;
    onSuccess?: () => void;
    onFailed?: () => void;
    onCancel: () => void;
    initialData?: AttorneyFeePopupData;
    settlementId: number;
    updatedData?: (data: AttorneyFeeEditFormSubmitProps) => void;
}
const useStyles = makeStyles()((theme: Theme) => ({
    popupContainer: {
        '& .MuiDialog-paper': {
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.primary.light,
            maxWidth: '550px',
            width: '100%',
            padding: '32px',
            boxSizing: 'border-box',
            borderRadius: '16px',
            maxHeight: '95%',
        },

        '& .MuiDialogTitle-root': {
            padding: 0,
            fontSize: '34px',
            fontWeight: 600,
            lineHeight: '120%',
            color: theme.palette.common.white,
            fontFamily: 'Poppins',
        },

        '& .MuiDialogContent-root': {
            padding: 0,
            display: 'flex',
            justifyContent: 'center',
            '&::-webkit-scrollbar': {
                width: '5px',
                borderRadius: '20px',
            },
            '&::-webkit-scrollbar-thumb': {
                borderRadius: 20,
                backgroundColor: theme.palette.secondary.dark,
            },
        },

        '& .MuiDialogContentText-root': {
            color: theme.palette.secondary.main,
        },

    },
    popupActions: {
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '416px',
        width: '100%',
        margin: '0 auto',
        gap: '16px',
        padding: 0,
    },
    popupButton: {
        color: theme.palette.info.main,

        '&.Mui-disabled': {
            color: 'rgba(255, 255, 255, 0.3)',
        },

        '&:hover': {
            background: 'rgba(144, 202, 249, 0.08)',
        },
    },
    formContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        maxWidth: '416px',
        width: '100%',
    },
    formFieldsWrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        alignItems: 'center',
        padding: '32px 0 32px 0',
    },
    checkboxWrapper: {
        width: '100%',

        '& label': {
            marginRight: 0,
        },
    },
}));


const EditDraftAttorneyFeePopup = ({
    isOpen,
    loading,
    onCancel,
    initialData,
    onSuccess,   
    updatedData,
}: EditAttorneyFeePopupProps) => {
    const { classes } = useStyles();
    const [isFormSubmitting, setIsFormSubmitting] = useState(false);

    const handleClose = () => {
        reset({
            ...ATTORNEY_FEE_EDIT_FORM_DEFAULT_VALUES,
            ...initialData,
        });
        onCancel();
    };

    const formMethods = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: yupResolver(attorneyFeeEditFormSchema),
        defaultValues: ATTORNEY_FEE_EDIT_FORM_DEFAULT_VALUES,
    });

    const {
        handleSubmit,
        reset,
        formState: { isValid, isDirty },
    } = formMethods;

    useEffect(() => {
        if (initialData) {
            reset({
                ...ATTORNEY_FEE_EDIT_FORM_DEFAULT_VALUES,
                ...initialData,
            });
        }
    }, [initialData, reset]);




    const onSubmitForm = (values: AttorneyFeeEditFormSubmitProps) => {        

        setIsFormSubmitting(true);
        try {         
           const formattedData = {
                ...values,
                fee: values.fee,
            };
            onSuccess && onSuccess();
            updatedData && updatedData(formattedData);
        } finally {
            setIsFormSubmitting(false);
        }
    };

    return (
        <Dialog className={classes.popupContainer} open={isOpen} onClose={handleClose} scroll="paper" >
            <Box display="flex" justifyContent="flex-end">
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    disableRipple
                    sx={(theme) => ({
                        color: theme.palette.common.white,
                    })}
                >
                    <CloseIcon />
                </IconButton>
            </Box>
            <DialogTitle>
                {'Edit Attorney'}
            </DialogTitle>
            {loading ? (
                <Box sx={{ margin: '30px' }}>
                    <Loader colorType={'warning'} />
                </Box>
            ) : (
                <>
                    <DialogContent>
                        <FormProvider {...formMethods}>
                            <Box className={classes.formContainer}>
                                <Box className={classes.formFieldsWrapper}>
                                    <FormTextField
                                        name="name"
                                        label="Name *"
                                    />

                                    <FormTextField
                                        name="fee"
                                        label="Fee *"
                                    />

                                    <FormSelectField
                                        name="role"
                                        label="Role"
                                        options={SECONDARY_FIRM_ROLES_OPTIONS}
                                        width="100%"
                                    />
                                </Box>

                                <DialogActions className={classes.popupActions}>
                                    <FormSubmitButton
                                        disabled={!isDirty || !isValid || isFormSubmitting}
                                        buttonText="Submit"
                                        onClick={handleSubmit(onSubmitForm)}
                                    />
                                    <FormCancelButton onClick={handleClose} />
                                </DialogActions>
                            </Box>
                        </FormProvider>
                    </DialogContent>

                </>
            )}
        </Dialog>
    );
};

export default EditDraftAttorneyFeePopup;